import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { useValueChanged } from "@/hooks/useValueChanged";

/**
 * Adopts a query param into local state when the URL changes externally
 * (back/forward, links) while ignoring echoes of this component's own writes.
 * `markWrite(value)` records the value most recently written to the URL;
 * until the URL carries it, other values arriving are earlier writes of the
 * same burst still landing, not navigation, so they are not adopted. A write
 * the URL already held (a no-op replace) completes on the very next render.
 *
 * Once the writer settles the URL is the truth again. The writer settles on
 * `window.location`, which `useSearchParams` can trail by a render, so the
 * settled value is read through `readSettled` rather than from `urlValue`.
 */
export function useUrlParamSync(t0) {
  const $ = _c(1);
  const {
    urlValue,
    isWriting,
    adopt,
    readSettled
  } = t0;
  const [pending, setPending] = useState(null);
  const urlChanged = useValueChanged(urlValue);
  const writerSettled = useValueChanged(isWriting) && !isWriting;
  if (pending !== null && Object.is(urlValue, pending.value)) {
    setPending(null);
  } else {
    if (urlChanged && pending === null) {
      adopt(urlValue);
    } else {
      if (writerSettled && pending !== null) {
        setPending(null);
        const settledValue = readSettled();
        if (!Object.is(settledValue, pending.value)) {
          adopt(settledValue);
        }
      }
    }
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = value => setPending({
      value
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}